import moment from 'moment';
import createSelector from 'selectorator';
import { constant, first, indexBy, sortBy, values } from 'underscore';

import getCommitStatus from '../../components/generic/Pill/getCommitStatus';
import { RevisionIssueScanState } from '../../types';
import { getQueryTeamId } from '../utils';
import type { Project, ProjectGroup, ProjectGroupRelease } from '../../types';

// Use state from projects page
const getFilterString = (_, props) => (props.location.query.title || '').toLowerCase();
const getSortMode = state => state.projects.sortMode;

type ProjectGroupEntities = Record<string, ProjectGroup | undefined>;
type ProjectEntities = Record<string, Project | undefined>;

type Sorter = (list: ProjectGroup[]) => ProjectGroup[];
const sorters = {
  stats: (list: ProjectGroup[]) => sortBy(list, group => {
    const { project_count = 0, license_count = 0, dependency_count = 0 } = group.latestRelease || {};
    return project_count + license_count + dependency_count;
  }),
  status: (list: ProjectGroup[]) => sortBy(list, group => {
    const { unresolved_issue_count = 0 } = group.latestRelease || {};
    return unresolved_issue_count;
  }),
  title: (list: ProjectGroup[]) => sortBy(list, 'title'),
  updatedAt: (list: ProjectGroup[]) => sortBy(list, 'updatedAt'),
};

/**
 * getEntityList is custom so that we can augment project groups with
 * their `latestRelease` properties.
 * This is so that we can use project data we've already fetched
 * for the projects that are part of the latest release in the group.
 */
const getEntityList = createSelector([
  'projectGroups.projectGroups',
  'projects.projects',
], (projectGroupEntities: ProjectGroupEntities, projects: ProjectEntities) => (
  values(projectGroupEntities).map(projectGroup => {
    const latestRelease = first(projectGroup.releases);
    if (!latestRelease) {
      return projectGroup;
    }

    let buildFailing = false;
    let buildNeedsConfig = false;
    let buildWaiting = false;

    /**
     * This function will map all the ProjectGroupProjectsTab from the latest release into
     * full Projects, using the ProjectEntities in the redux state.
     * While we're mapping anyway, go ahead and record the number of issues and the overall
     * build status of the project so that we don't have to recompute in other components.
     */
    const augmentedProjects = latestRelease.projects.map(p => {
      const project = projects[p.projectId];
      if (!project) {
        return null;
      }

      const latest = first(project.revisions);
      if (!latest) {
        return project;
      }

      const status = getCommitStatus(latest);

      switch (status) {
        case RevisionIssueScanState.IssuesFound:
          buildFailing = true;
          break;
        case RevisionIssueScanState.Waiting:
          buildWaiting = true;
          break;
        case RevisionIssueScanState.NeedsConfiguration:
          buildNeedsConfig = true;
          break;
        default:
      }

      return project;
    })
      .filter(p => !!p);

    // Choose the overall build state based on what the user should probably see.
    let overallScanState: RevisionIssueScanState;
    if (buildFailing) {
      overallScanState = RevisionIssueScanState.IssuesFound;
    } else if (buildNeedsConfig) {
      overallScanState = RevisionIssueScanState.NeedsConfiguration;
    } else if (buildWaiting) {
      overallScanState = RevisionIssueScanState.Waiting;
    } else {
      overallScanState = RevisionIssueScanState.Passing;
    }

    return {
      ...projectGroup,
      latestRelease: {
        ...latestRelease,
        projects: augmentedProjects,
        scanStatus: overallScanState,
        scans: latestRelease.scans,
      },
    };
  })
));

/**
 * here we use `getEntityList` as the base for `getEntities`, not the other way around. The reason is that it's more efficient
 * to perform the operations in this order for this set of data:
 * We need to convert the entities to an array already to augment with the latest release,
 * so if we inherited in this order we can sometimes skip an object -> array -> object conversion.
 */
const getEntities = createSelector([getEntityList], (entities: ProjectGroup[]) => indexBy(entities, 'id'));

/**
 * Since we already have custom `getEntities` and `getEntityList` selectors,
 * just implement getStatus here and don't waste time creating two functions we won't use.
 */
const getStatus = createSelector([`projectGroups.status`]);

export const getSelectedReleaseId = createSelector<unknown, number>(['projectGroups.selectedReleaseId']);

// filter project groups by title
const getFiltered = createSelector(
  [getEntityList, getFilterString, getQueryTeamId],
  (list: ProjectGroup[], filter = '', teamId: string | undefined) => {
    const teamCond = teamId
      ? ({ releaseGroupTeams }: ProjectGroup) => releaseGroupTeams?.some(groupTeam => groupTeam.teamId === Number(teamId))
      : constant(true);

    return list.filter(group => group.title.toLowerCase().includes(filter) && teamCond(group));
  },
);

const getFilteredCount = createSelector(
  [getFiltered],
  projects => projects.length,
);

const getSorted = createSelector(
  [getFiltered, getSortMode],
  (list: ProjectGroup[], sortMode: string = '') => {
    const sorter: Sorter = sorters[sortMode.slice(sortMode.indexOf('-') + 1, sortMode.length)];
    if (!sorter) {
      return list;
    }

    const sorted = sorter(list);
    if (sortMode.includes('-')) {
      sorted.reverse();
    }
    return sorted;
  },
);

const getSelected = createSelector(
  [getSorted, 'projectGroups.selectedProjectGroupIds'],
  (list: ProjectGroup[], selectedIds = {}) =>
    list.filter(group => !!selectedIds[group.id]),
);

const getRowsForTable = createSelector(
  [getSorted, 'projectGroups.selectedProjectGroupIds'],
  (list: ProjectGroup[], selectedIds = {}) => list.map(group => {
    const selected = !!selectedIds[group.id];
    return { ...group, selected };
  }),
);

const getRelease = createSelector<unknown, ProjectGroupRelease>(['projectGroups.release.data']);
const getIsReportPublished = createSelector([getRelease], (release: ProjectGroupRelease) => !!release.publishedAt);
const getReportLastPublishedAt = createSelector([getRelease], (release: ProjectGroupRelease) => {
  if (!release.publishedAt) {
    return null;
  }
  const lastPublished = new Date(release.publishedAt);
  return moment(lastPublished).format('MMMM Do YYYY, h:mm:ss a');
});
const getReportPublishedStatus =
  createSelector([getRelease], (release: ProjectGroupRelease) => release.publishedOnPortal === 'pending' ? 'Pending...' : 'Never published');

export default {
  getStatus,
  getEntities,
  getEntityList,
  getSelected,
  getFiltered,
  getFilteredCount,
  getSortMode,
  getSorted,
  getRowsForTable,
  getCurrentProjectGroup: (state, props) => state.projectGroups.projectGroups[props.params.projectGroupId],
  getRelease,
  getSelectedReleaseId,
  getIsReportPublished,
  getReportLastPublishedAt,
  getReportPublishedStatus,
  getReleaseIsLoading: createSelector(['projectGroups.release.status.loading']),
  getReleaseError: createSelector(['projectGroups.release.status.error']),
  getSelectedRelease: (state, props) => {
    if (!state.projectGroups.selectedReleaseId) {
      return null;
    }
    const projectGroup = state.projectGroups.projectGroups[props.params.projectGroupId];
    if (projectGroup) {
      return projectGroup.releases.find(r => r.id === state.projectGroups.selectedReleaseId);
    }
    return null;
  },
  getReleaseScans: createSelector(['projectGroups.releaseScans.data']),
  getReleaseScanStatus: createSelector(['projectGroups.releaseScans.status']),
  getSelectedScan: ({ projectGroups }) =>
    projectGroups.releaseScans.data?.find(scan => scan.id === projectGroups.selectedScanId),
  getSelectedScanId: createSelector(['projectGroups.selectedScanId']),
  getReleaseSummary: createSelector(['projectGroups.releaseSummary.data']),
  getReleaseSummaryStatus: createSelector(['projectGroups.releaseSummary.status']),
  getFetchingAllProjectsInGroupStatus: createSelector(['projectGroups.fetchingAllProjectsInGroupStatus']),
  getPollRelease: createSelector(['projectGroups.pollRelease'], pollRelease => pollRelease[0]),
  getProjectGroupById: (
    state: { projectGroups: { projectGroups: Record<string, ProjectGroup | undefined> } },
    projectGroupId: string,
  ): ProjectGroup | undefined =>
    state.projectGroups.projectGroups[projectGroupId],
};
